import React from 'react';

import { Button, Stack, Typography } from '@mui/material';

import { LENS_PERIOD_FILTER_ID, LENS_PERIOD_KEY, type LensFilterValues, type LensPeriod } from '~/types/leLens';

import type { LensSelection } from './cardView';
import type { LensState } from './document/types';
import { isLensPeriod, mergePeriods } from './lensPeriodPresets';

export type LensFiltered = {
  lensId: string;
  filters: LensFilterValues;
  selections: LensSelection;
  clear: boolean;
  state?: Pick<LensState, 'period' | 'filters'>;
};

export function applyChatLensFilters(receipt: LensFiltered) {
  window.dispatchEvent(new CustomEvent('lens-view-filter', { detail: receipt }));
}

function rangePeriod(value: LensFilterValues[string] | undefined): Partial<LensPeriod> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value) || !('from' in value) || !('to' in value)) return undefined;
  return typeof value.from === 'string' && typeof value.to === 'string'
    ? { window: { from: value.from, to: value.to } }
    : undefined;
}

export function mergeChatLensFilters(current: LensFilterValues, patch: LensFilterValues, board: LensPeriod): LensFilterValues {
  const next = { ...current, ...patch };
  const period = isLensPeriod(patch[LENS_PERIOD_KEY]) ? patch[LENS_PERIOD_KEY] : undefined;
  const range = rangePeriod(patch[LENS_PERIOD_FILTER_ID]);
  if (period || range) {
    const prior = isLensPeriod(current[LENS_PERIOD_KEY]) ? current[LENS_PERIOD_KEY] : rangePeriod(current[LENS_PERIOD_FILTER_ID]);
    next[LENS_PERIOD_KEY] = mergePeriods(board, prior, range, period);
    delete next[LENS_PERIOD_FILTER_ID];
  }
  return next;
}

export default function LensFilterReceipt({ receipt }: { receipt: LensFiltered }) {
  return (
    <Stack alignItems="center" direction="row" spacing={0.5} data-testid="lens-filter-receipt">
      <Typography variant="body2">{receipt.clear ? 'Cleared this view' : 'Filtered this view'}</Typography>
      {!receipt.clear && (
        <>
          <Typography aria-hidden="true" variant="body2">·</Typography>
          <Button onClick={() => applyChatLensFilters({ ...receipt, filters: {}, selections: {}, clear: true })} size="small">
            Clear
          </Button>
        </>
      )}
    </Stack>
  );
}
